// The review queue's groups and bulk reject (11 Oct 2026). Pure helpers, so the rules are tested
// without the screen.

// `live` hides the rows the legacy import wrote; `legacy` lists only those. The service counts both
// for the whole market whatever group is asked, so every answer fills every tab's count.
export const REVIEW_GROUPS = [
  { key: "live", label: "Needs review" },
  { key: "legacy", label: "Old data cleanup" },
  { key: "all", label: "All" },
];
export const DEFAULT_REVIEW_GROUP = "live";

// The service's largest page and the most registrations one bulk reject takes.
export const REVIEW_PAGE_SIZE = 100;
export const BULK_REJECT_MAX = 100;

export const groupCount = (counts, group) => {
  const live = Number.isInteger(counts?.live) ? counts.live : null;
  const legacy = Number.isInteger(counts?.legacy) ? counts.legacy : null;
  if (group === "live") {
    return live;
  }
  if (group === "legacy") {
    return legacy;
  }
  return live !== null && legacy !== null ? live + legacy : null;
};

// An ownership claim is decided on its own (it names a claimant): the bulk reject would reject the
// registration, not the claim, so a claim row is never selected.
export const isSelectable = (item) => item?.reason !== "ownership_claim";

// The rows "select all" takes: the selectable rows shown, at most BULK_REJECT_MAX of them.
export const selectableIds = (items) =>
  items.filter(isSelectable).slice(0, BULK_REJECT_MAX).map((item) => item.id);

// Keeps only the selected rows still on screen (after a reload or a removal).
export const keepShown = (selected, items) => {
  const shown = new Set(items.map((item) => item.id));
  return new Set([...selected].filter((id) => shown.has(id)));
};

// The registrations of the selected rows, each once and in the order shown.
export const registrationIdsOf = (selected, items) => {
  const ids = [];
  for (const item of items) {
    if (selected.has(item.id) && isSelectable(item) && !ids.includes(item.registrationId)) {
      ids.push(item.registrationId);
    }
  }
  return ids;
};

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// One sentence for the person after a bulk reject.
export const bulkSummary = (data) => {
  const rejected = Number.isInteger(data?.rejected) ? data.rejected : 0;
  const skipped = Number.isInteger(data?.skipped) ? data.skipped : 0;
  const failed = Number.isInteger(data?.failed) ? data.failed : 0;
  const parts = [`${plural(rejected, "registration", "registrations")} rejected`];
  if (skipped > 0) {
    parts.push(`${skipped} skipped (no longer waiting for a decision)`);
  }
  if (failed > 0) {
    parts.push(`${failed} not rejected`);
  }
  return `${parts.join(", ")}.`;
};

// The frame numbers of the registrations that were not rejected, with their code, for the banner.
export const failedRows = (data, items) => {
  const frameOf = new Map(items.map((item) => [item.registrationId, item.frameNumber]));
  return (Array.isArray(data?.results) ? data.results : [])
    .filter((result) => result?.outcome === "failed")
    .map((result) => ({
      registrationId: result.registrationId,
      frameNumber: frameOf.get(result.registrationId) || result.registrationId,
      code: result.code || "",
    }));
};
